import { ChevronRight, PanelLeft } from "lucide-react";
import {
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { cn } from "./cn";
import {
  CabecalhoDaLateralComProjeto,
  SeletorDeProjeto,
  type ProjetoDaBarra,
} from "./seletor-de-projeto";

export type ItemDeNavegacao = {
  readonly rotulo: string;
  readonly href: string;
  readonly icone?: ReactNode;
  readonly ativo?: boolean;
  readonly contador?: number | undefined;
  readonly grupo?: string | undefined;
  /** Item visivel, sem navegacao (modulos ainda nao liberados). */
  readonly desabilitado?: boolean | undefined;
  /** Subitens (ex.: Agenda > Falhas). */
  readonly filhos?: readonly ItemDeNavegacao[] | undefined;
};

export type PropsDaMolduraDoApp = {
  readonly nomeDoApp: string;
  readonly marca?: ReactNode;
  readonly titulo: string;
  readonly subtitulo?: string | undefined;
  readonly acoes?: ReactNode;
  readonly navegacao?: readonly ItemDeNavegacao[];
  /**
   * Projetos/tenants para o TeamSwitcher no topo (sidebar-07).
   * Quando informado, substitui o bloco da marca pelo seletor.
   */
  readonly projetos?: readonly ProjetoDaBarra[];
  readonly projetoAtivoId?: string;
  readonly onMudarProjeto?: (projetoId: string) => void;
  readonly rotuloListaProjetos?: string;
  readonly onCriarProjeto?: () => void | undefined;
  readonly rotuloCriarProjeto?: string | undefined;
  readonly onConfigurarProjeto?: () => void | undefined;
  readonly rotuloConfigurarProjeto?: string | undefined;
  readonly onExcluirProjeto?: () => void | undefined;
  readonly rotuloExcluirProjeto?: string | undefined;
  /**
   * Pé da lateral. Pode ser nó fixo ou função que recebe se a barra está
   * só em ícones — para o menu de usuário caber no colapso.
   */
  readonly rodapeDaLateral?:
    | ReactNode
    | ((estado: { readonly soIcone: boolean }) => ReactNode);
  readonly children: ReactNode;
};

const CHAVE_COLAPSADA = "renke-refs.sidebar-colapsada";
const LARGURA = "15.5rem";
const LARGURA_ICONE = "4.5rem";

/**
 * Shell do design system Renke × Workflow.
 * - Sidebar em tom próprio; item ativo em amarelo, contadores em tinta.
 * - Painel principal arredondado (28px) com "aba": o título fica na aba e as
 *   ações ocupam o degrau à direita, sobre a cor do app.
 */
export function MolduraDoApp({
  nomeDoApp,
  marca,
  titulo,
  subtitulo,
  acoes,
  navegacao = [],
  projetos,
  projetoAtivoId,
  onMudarProjeto,
  rotuloListaProjetos = "Projetos",
  onCriarProjeto,
  rotuloCriarProjeto,
  onConfigurarProjeto,
  rotuloConfigurarProjeto,
  onExcluirProjeto,
  rotuloExcluirProjeto,
  rodapeDaLateral,
  children,
}: PropsDaMolduraDoApp) {
  const [gavetaAberta, definirGavetaAberta] = useState(false);
  const [colapsada, definirColapsada] = useState(() => {
    try {
      return globalThis.localStorage?.getItem(CHAVE_COLAPSADA) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(
        CHAVE_COLAPSADA,
        colapsada ? "1" : "0",
      );
    } catch {
      // ignore
    }
  }, [colapsada]);

  const alternarColapsoDesktop = () => {
    definirColapsada((valor) => !valor);
  };

  const grupos = agruparNavegacao(navegacao);
  const comSeletor =
    typeof onMudarProjeto === "function" &&
    (Boolean(projetos?.length) || typeof onCriarProjeto === "function");

  const lateral = (modo: "desktop" | "mobile") => {
    const soIcone = modo === "desktop" && colapsada;
    return (
      <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
        <div className="flex shrink-0 flex-col">
          {comSeletor && onMudarProjeto ? (
            <CabecalhoDaLateralComProjeto
              soIcone={soIcone}
              seletor={
                <SeletorDeProjeto
                  projetos={projetos ?? []}
                  projetoAtivoId={projetoAtivoId ?? ""}
                  onMudar={onMudarProjeto}
                  soIcone={soIcone}
                  rotuloLista={rotuloListaProjetos}
                  {...(onCriarProjeto ? { onCriar: onCriarProjeto } : {})}
                  {...(rotuloCriarProjeto
                    ? { rotuloCriar: rotuloCriarProjeto }
                    : {})}
                  {...(onConfigurarProjeto
                    ? { onConfigurar: onConfigurarProjeto }
                    : {})}
                  {...(rotuloConfigurarProjeto
                    ? { rotuloConfigurar: rotuloConfigurarProjeto }
                    : {})}
                  {...(onExcluirProjeto ? { onExcluir: onExcluirProjeto } : {})}
                  {...(rotuloExcluirProjeto
                    ? { rotuloExcluir: rotuloExcluirProjeto }
                    : {})}
                />
              }
            />
          ) : (
            <>
              <div
                className={cn(
                  "flex items-center overflow-hidden",
                  soIcone ? "justify-center px-2 py-5" : "px-5 pb-5 pt-6",
                )}
              >
                {soIcone ? (
                  <span
                    className="grid size-10 place-items-center rounded-lg bg-primary text-sm font-bold tracking-tight text-primary-foreground"
                    title={nomeDoApp}
                  >
                    {nomeDoApp.trim().slice(0, 1).toUpperCase() || "R"}
                  </span>
                ) : (
                  <div className="min-w-0 w-full [&_.logo-renke-refs]:h-5 [&_.logo-renke-refs]:max-w-[10.5rem]">
                    {marca ?? (
                      <span className="truncate text-sm font-semibold tracking-tight">
                        {nomeDoApp}
                      </span>
                    )}
                  </div>
                )}
              </div>

            </>
          )}
        </div>

        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-3 pb-3",
            soIcone ? "items-center pt-3" : "pt-2",
          )}
        >
          {grupos.map((grupo) => (
            <div key={grupo.rotulo}>
              {!soIcone ? (
                <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase leading-3 tracking-[0.06em] text-muted-foreground">
                  {grupo.rotulo}
                </p>
              ) : null}
              <nav
                aria-label={grupo.rotulo}
                className="flex flex-col gap-0.5"
              >
                {grupo.itens.map((item) => (
                  <ItemDaNavegacaoLateral
                    key={item.href}
                    item={item}
                    soIcone={soIcone}
                    onNavegar={() => {
                      if (modo === "mobile") definirGavetaAberta(false);
                    }}
                  />
                ))}
              </nav>
            </div>
          ))}
        </div>

        {rodapeDaLateral ? (
          <div
            className={cn(
              "mt-auto border-t border-sidebar-border p-3",
              soIcone && "flex justify-center",
            )}
          >
            {typeof rodapeDaLateral === "function"
              ? rodapeDaLateral({ soIcone })
              : rodapeDaLateral}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className="flex h-svh max-h-svh w-full overflow-hidden bg-background"
      style={
        {
          "--sidebar-largura": LARGURA,
          "--sidebar-largura-icone": LARGURA_ICONE,
        } as CSSProperties
      }
    >
      <aside
        className={cn(
          "relative hidden shrink-0 transition-[width] duration-200 ease-out md:block",
          colapsada
            ? "w-[var(--sidebar-largura-icone)]"
            : "w-[var(--sidebar-largura)]",
        )}
      >
        <div
          className="fixed inset-y-0 left-0 z-20 hidden h-svh md:flex md:flex-col"
          style={{
            width: colapsada ? LARGURA_ICONE : LARGURA,
          }}
        >
          {lateral("desktop")}
          <button
            type="button"
            aria-label={
              colapsada ? "Expandir barra lateral" : "Recolher barra lateral"
            }
            title={
              colapsada ? "Expandir barra lateral" : "Recolher barra lateral"
            }
            onClick={alternarColapsoDesktop}
            className={cn(
              "group/divisor absolute inset-y-0 -right-1.5 z-30 hidden w-3 cursor-col-resize md:block",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "absolute inset-y-0 left-1/2 w-px -translate-x-1/2",
                "bg-transparent transition-[background-color,width] duration-200",
                "group-hover/divisor:w-0.5 group-hover/divisor:bg-border-strong",
                "group-focus-visible/divisor:w-0.5 group-focus-visible/divisor:bg-foreground",
              )}
            />
          </button>
        </div>
      </aside>

      {gavetaAberta ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Fechar navegação"
            onClick={() => definirGavetaAberta(false)}
            className="absolute inset-0 cursor-pointer bg-overlay backdrop-blur-[2px]"
          />
          <div
            id="navegacao-lateral"
            className="animate-subir absolute inset-y-0 left-0 w-[var(--sidebar-largura)] bg-sidebar shadow-e3"
          >
            {lateral("mobile")}
          </div>
        </div>
      ) : null}

      <div className="relative flex h-svh max-h-svh min-w-0 flex-1 flex-col overflow-hidden bg-background p-2 md:p-3">
        <header className="flex shrink-0 items-end gap-3">
          <div className="rk-shell__aba flex min-w-0 items-center gap-3 px-3 pb-1 pt-3 md:px-5 md:pt-4">
            <button
              type="button"
              aria-label={
                gavetaAberta || !colapsada
                  ? "Alternar barra lateral"
                  : "Expandir barra lateral"
              }
              aria-expanded={gavetaAberta || !colapsada}
              aria-controls="navegacao-lateral"
              onClick={() => {
                if (globalThis.matchMedia("(min-width: 768px)").matches) {
                  alternarColapsoDesktop();
                } else {
                  definirGavetaAberta((v) => !v);
                }
              }}
              className="grid size-8 shrink-0 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-sunken hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/30"
            >
              <PanelLeft className="size-[18px]" strokeWidth={1.75} aria-hidden="true" />
            </button>
            <div className="min-w-0 py-1">
              <h1 className="truncate text-xl font-semibold leading-7 tracking-[-0.01em] text-foreground">
                {titulo}
              </h1>
              {subtitulo ? (
                <p className="truncate text-xs leading-4 text-muted-foreground">
                  {subtitulo}
                </p>
              ) : null}
            </div>
          </div>

          {acoes ? (
            <div className="ml-auto flex min-w-0 shrink-0 items-center gap-2 self-center overflow-x-auto pb-1 pl-8">
              {acoes}
            </div>
          ) : null}
        </header>

        <div className="rk-shell__corpo relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <main className="mx-auto flex w-full max-w-7xl min-w-0 flex-col gap-6 px-4 py-5 sm:px-6 sm:py-6">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}

type GrupoNav = {
  readonly rotulo: string;
  readonly itens: readonly ItemDeNavegacao[];
};

function classesDoItemNav(entrada: {
  readonly soIcone: boolean;
  readonly ativo?: boolean;
  readonly desabilitado?: boolean;
  readonly denso?: boolean;
}): string {
  return cn(
    "group/nav relative flex items-center gap-3 rounded-lg text-[13px] font-medium tracking-[-0.005em] transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/30",
    "[&_svg]:size-[18px] [&_svg]:shrink-0 [&_svg]:stroke-[1.75]",
    entrada.soIcone
      ? "size-10 justify-center self-center"
      : entrada.denso
        ? "h-9 px-3"
        : "h-10 px-3",
    entrada.desabilitado
      ? "cursor-not-allowed text-muted-foreground/50"
      : entrada.ativo
        ? "cursor-pointer bg-sidebar-primary font-semibold text-sidebar-primary-foreground [&_svg]:text-sidebar-primary-foreground"
        : "cursor-pointer text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground [&_svg]:text-muted-foreground hover:[&_svg]:text-sidebar-accent-foreground",
  );
}

function ContadorNav({ valor }: { readonly valor: number }) {
  return (
    <span className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-ink px-1.5 text-[11px] font-semibold tabular-nums text-primary">
      {valor}
    </span>
  );
}

function ItemDaNavegacaoLateral({
  item,
  soIcone,
  onNavegar,
}: {
  readonly item: ItemDeNavegacao;
  readonly soIcone: boolean;
  readonly onNavegar: () => void;
}) {
  const filhos = item.filhos ?? [];
  const temFilhos = filhos.length > 0;
  const filhoAtivo = filhos.some((f) => f.ativo);
  const [submenuAberto, definirSubmenuAberto] = useState(
    () => Boolean(item.ativo || filhoAtivo),
  );

  useEffect(() => {
    if (item.ativo || filhoAtivo) definirSubmenuAberto(true);
  }, [item.ativo, filhoAtivo]);

  const classes = classesDoItemNav({
    soIcone,
    ativo: Boolean(item.ativo && !filhoAtivo),
    desabilitado: Boolean(item.desabilitado),
  });

  if (item.desabilitado) {
    return (
      <span
        title={soIcone ? `${item.rotulo} (em breve)` : "Em breve"}
        aria-disabled="true"
        className={classes}
      >
        {item.icone}
        {!soIcone ? <span className="truncate">{item.rotulo}</span> : null}
      </span>
    );
  }

  if (!temFilhos || soIcone) {
    return (
      <a
        href={item.href}
        title={soIcone ? item.rotulo : undefined}
        aria-current={item.ativo ? "page" : undefined}
        onClick={onNavegar}
        className={classes}
      >
        {item.icone}
        {!soIcone ? (
          <>
            <span className="truncate">{item.rotulo}</span>
            {item.contador !== undefined ? (
              <ContadorNav valor={item.contador} />
            ) : null}
          </>
        ) : null}
      </a>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-0.5">
        <a
          href={item.href}
          aria-current={item.ativo && !filhoAtivo ? "page" : undefined}
          onClick={onNavegar}
          className={cn(classes, "min-w-0 flex-1")}
        >
          {item.icone}
          <span className="truncate">{item.rotulo}</span>
          {item.contador !== undefined ? (
            <ContadorNav valor={item.contador} />
          ) : null}
        </a>
        <button
          type="button"
          aria-expanded={submenuAberto}
          aria-label={
            submenuAberto
              ? `Recolher ${item.rotulo}`
              : `Expandir ${item.rotulo}`
          }
          onClick={() => definirSubmenuAberto((v) => !v)}
          className={cn(
            "grid size-10 shrink-0 cursor-pointer place-items-center rounded-lg text-muted-foreground transition-colors duration-150",
            "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/30",
          )}
        >
          <ChevronRight
            className={cn(
              "size-4 transition-transform duration-200",
              submenuAberto && "rotate-90",
            )}
            aria-hidden
          />
        </button>
      </div>
      {submenuAberto ? (
        <div
          className="ml-5 flex flex-col gap-0.5 border-l border-sidebar-border pl-2"
          role="group"
          aria-label={`Submenu de ${item.rotulo}`}
        >
          {filhos.map((filho) => (
            <a
              key={filho.href}
              href={filho.href}
              aria-current={filho.ativo ? "page" : undefined}
              onClick={onNavegar}
              className={classesDoItemNav({
                soIcone: false,
                ativo: Boolean(filho.ativo),
                denso: true,
              })}
            >
              {filho.icone}
              <span className="truncate">{filho.rotulo}</span>
              {filho.contador !== undefined ? (
                <ContadorNav valor={filho.contador} />
              ) : null}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function agruparNavegacao(
  itens: readonly ItemDeNavegacao[],
): readonly GrupoNav[] {
  const ordem: string[] = [];
  const mapa = new Map<string, ItemDeNavegacao[]>();

  for (const item of itens) {
    const grupo = item.grupo?.trim() || "Menu";
    if (!mapa.has(grupo)) {
      mapa.set(grupo, []);
      ordem.push(grupo);
    }
    mapa.get(grupo)?.push(item);
  }

  return ordem.map((rotulo) => ({
    rotulo,
    itens: mapa.get(rotulo) ?? [],
  }));
}
